import {
  base_colorContentBorder,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_family_weight_regular,
  font_family_weight_semibold,
  font_md,
  font_size_md,
  font_size_sm,
  font_sm,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

/**
 * Matches `CardBudgetsTable.style` and `CardOverviewRecentTransactions.style` so
 * the three tables stacked on the Overview tab read as one surface: vertical
 * padding trimmed from `ContentCard`'s prose-sized default, but SYMMETRIC — the
 * bottom gap matches the top one above the heading.
 */
export const panel = css`
  display: flex;
  flex-direction: column;
  padding: ${spacing(6)} ${spacing(8)};
`

export const header = css`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing(4)};
  margin-bottom: ${spacing(3)};
`

/**
 * The uppercase section kicker shared with the sibling Overview panels.
 * Uppercased in CSS, never in the translation source, so the accessible name
 * stays the plain phrase.
 */
export const title = css`
  font-size: ${font_size_sm};
  font-family: ${font_family_weight_semibold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${text_colorTextSecondary};
  margin: 0;
`

/**
 * The right-hand end of the heading row: the "view all" link and the New
 * virtual card CTA sit together there, so they need their own row box rather
 * than each being a bare flex child of the space-between header.
 */
export const headerActions = css`
  display: flex;
  align-items: center;
  gap: ${spacing(4)};
`

/** The "N virtual cards" count, for the future Virtual Cards tab. */
export const count = css`
  font-size: ${font_size_sm};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
  white-space: nowrap;
`

/**
 * A column header, rendered by our own `renderHeader` rather than styled through
 * `.MuiDataGrid-columnHeaderTitle` — MUI wraps that title in its own
 * emotion-styled element, so restyling it from outside is a cascade race we lose
 * intermittently.
 *
 * Uppercased in CSS, never in the translation source, so the accessible name —
 * which MUI takes from `headerName` — stays the plain phrase.
 */
export const columnHeader = css`
  font-size: ${font_size_sm};
  font-family: ${font_family_weight_medium};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${text_colorTextSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/** The terminal column's header, right-aligned over its values. */
export const columnHeaderRight = css`
  ${columnHeader};
  width: 100%;
  text-align: right;
`

/** Keeps the grid's own top border from doubling the card's inner spacing. */
export const grid = css`
  border-top: 1px solid ${base_colorContentBorder};

  /*
    Terminal cards — cancelled, expired, closed, rejected, consumed — dim, per
    the design. Applied to the ROW rather than each cell so the whole record
    recedes together, and left above the hover tint so pointing at a dimmed row
    still lights it up.
  */
  .MuiDataGrid-row.dimmed {
    opacity: 0.6;
  }
`

/**
 * Cardholder cell wrapper.
 *
 * TWO elements need `min-width: 0`, not one, and missing the second is why the
 * first attempt at this fix changed nothing.
 *
 * `UserAvatarAndName` renders its own outer flex row, which becomes a flex ITEM
 * of this wrapper. A flex item defaults to `min-width: auto` — it refuses to
 * shrink below its content — so that row stayed as wide as the untruncated
 * email no matter what overflow rules the text inside carried, and ran under
 * the Type column. The child rule below is what actually lets it shrink; the
 * `nowrap` passed via `textCss` is then what turns the overflow into an
 * ellipsis.
 *
 * Reached by selector rather than by prop because `UserAvatarAndName` declares
 * a `className` prop and never applies it to anything — so a `className` or
 * styled-components `css` prop on that component is silently dropped.
 */
export const cardholderCell = css`
  display: flex;
  align-items: center;
  min-width: 0;
  width: 100%;

  /* The component's own outer flex row — see above. */
  > * {
    min-width: 0;
    flex: 1 1 auto;
  }
`

/**
 * Passed to `UserAvatarAndName`'s `textCss`. `white-space: nowrap` is the
 * missing half of that component's own ellipsis rules; without it the name
 * wraps and the ellipsis never triggers. Inherited by the "(unregistered)"
 * line below the name, which needs the same treatment.
 */
export const cardholderText = css`
  min-width: 0;
  white-space: nowrap;
`

/** Card cell: the card-type glyph beside the name/last-four stack. */
export const nameCell = css`
  display: flex;
  align-items: center;
  gap: ${spacing(2)};
  min-width: 0;
`

export const nameGlyph = css`
  height: 20px;
  flex: 0 0 auto;
`

/**
 * Name over masked last four, per the design.
 *
 * `overflow: hidden` and the centred justification matter: this is the only
 * two-line cell in the table, so it is the one that can outgrow the 52px row.
 */
export const nameStack = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  overflow: hidden;
`

/**
 * The masked last four under the card name.
 *
 * `font_sm`, NOT the bare `font_size_sm` token: the mixin carries `line-height`
 * and the token is the size alone. Without it the line-height inherits from the
 * page, and the two lines of this stack together overflow the row — the name
 * clipped above the row border and the last four below it.
 */
export const nameSub = css`
  ${font_sm};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/**
 * The card-name anchor.
 *
 * `text-decoration: none` is load-bearing, not cosmetic: an `<a>` underlines by
 * default, and the design's links carry no underline at rest and underline on
 * hover only (`.gf-textlink` in the design render). Without this the row name
 * would sit underlined at rest — backwards from the design, and different from
 * the sibling `CardBudgetsTable` row on the same page.
 *
 * Same treatment as `CardBudgetsTable.style.nameLink` and `StatNavLink`'s
 * `navLink`, so every link on the tab reads as one idiom. `color: inherit` keeps
 * it reading as a table row rather than a link in prose, while still being a
 * real `<a href>` for ⌘-click, copy-link, hover preview and screen-reader link
 * enumeration.
 *
 * `font_md` rather than the bare `font_size_md` token, and `display: block` —
 * see `nameSub` for the line-height reason; the block is what makes
 * `text-overflow: ellipsis` apply to an anchor at all.
 */
export const nameLink = css`
  ${font_md};
  display: block;
  font-family: ${font_family_weight_regular};
  color: inherit;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`

/** A plain text cell, matching `CardBudgetsTable`'s. */
export const textCell = css`
  font-size: ${font_size_md};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/**
 * The terminal "spent / limit" pair. Tabular figures so the slash lines up down
 * the column, which is the whole reason the design pairs them in one cell.
 */
export const amountCell = css`
  width: 100%;
  text-align: right;
  font-size: ${font_size_md};
  font-variant-numeric: tabular-nums;
  color: ${text_colorTextPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/**
 * Shared frame for the loading / empty / error bodies so the panel keeps a
 * stable height as it moves between them and the sections around it don't jump.
 */
export const statusBody = css`
  ${font_md};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: ${spacing(24)};
  margin: 0;
`
